/**
 * A Reel's kind: Game Market Reel or Player Market Reel (GL-08, CAE-1593).
 *
 * A Reel declares its kind when it is created, so a Reel nobody has created yet
 * asks for one here, before its first write: renaming it or adding outcomes to it
 * creates it as the kind chosen. It starts at the kind the Sport's required mix
 * gives that Reel number (`defaultReelKind`). Once the Reel exists the choice
 * gives way to a caption, since the kind is fixed from publish and publish holds
 * the Reel's options to it (S2W-D06-03).
 */
import Box from '@mui/material/Box';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import type { Theme } from '@mui/material/styles';
import type { ReelKind as ReelKindValue } from '../../../../api/contests';

const KIND_LABELS: Record<ReelKindValue, string> = {
  game: 'Game Market',
  player: 'Player Market',
};

export interface ReelKindProps {
  /** The Reel's own kind, when it exists; undefined for a Reel not yet created. */
  existingKind: ReelKindValue | undefined;
  /** The kind a Reel not yet created will be created as. */
  newReelKind: ReelKindValue;
  editable: boolean;
  onChoose: (kind: ReelKindValue) => void;
}

export const ReelKind = ({ existingKind, newReelKind, editable, onChoose }: ReelKindProps) => {
  if (existingKind !== undefined) {
    return (
      <Box
        sx={(theme: Theme) => ({ fontSize: '13px', color: theme.tokens.color.ink3 })}
      >{`${KIND_LABELS[existingKind]} Reel`}</Box>
    );
  }

  return (
    <Box>
      <Box
        id="reel-kind-label"
        sx={(theme: Theme) => ({
          fontSize: '13px',
          fontWeight: 600,
          color: theme.tokens.color.ink2,
        })}
      >
        Reel kind
      </Box>
      <RadioGroup
        row
        aria-labelledby="reel-kind-label"
        value={newReelKind}
        onChange={(event) => onChoose(event.target.value as ReelKindValue)}
      >
        {(Object.keys(KIND_LABELS) as ReelKindValue[]).map((kind) => (
          <FormControlLabel
            key={kind}
            value={kind}
            control={<Radio size="small" />}
            label={KIND_LABELS[kind]}
            disabled={!editable}
          />
        ))}
      </RadioGroup>
    </Box>
  );
};
